package com.appsyde.shared.presentation.style.style

import com.appsyde.shared.presentation.style.token.AppStylesTokens
import com.appsyde.shared.presentation.style.token.AppStylesTokens.INK_SOFT
import com.appsyde.shared.presentation.style.token.AppStylesTokens.INK_VERY_SOFT
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingBottom
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.css.rgba

/**
 * Styles de la sidebar gauche (Respond) : carte Météo dominante + carte Dossier (Données / Documents).
 * Hiérarchie visuelle et continuité entre les blocs.
 */
object AppStylesLeftSidebar : StyleSheet() {

    /** Espacement renforcé entre carte Météo et carte Dossier. */
    val sidebarGap by style {
        gap(20.px)
    }

    /** Bloc titre + Client regroupés sans espace (sans toucher à panelTitleWrap ailleurs). */
    val sidebarDossierHeaderBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        property("min-width", "0")
    }

    /** Titre « Mémoire / Dossier complet » : supprime tout espace en bas pour coller au bloc Client. */
    val sidebarDossierTitleInHeader by style {
        marginBottom(0.px)
        paddingBottom(0.px)
    }

    /** Kicker (Synthèse, Ticket) : label petit, discret, pas au même niveau que le titre. */
    val sidebarCardKicker by style {
        property("font-size", "11px")
        property("font-weight", "650")
        property("letter-spacing", "-0.004em")
        property("color", "rgba(102,96,230,0.72)")
    }

    /** Titre de section (Dossier, Contexte) : affirmé, vrai titre. */
    val sidebarCardTitle by style {
        property("font-size", "14px")
        property("font-weight", "550")
        property("letter-spacing", "-0.012em")
        property("color", INK_SOFT)
    }

    /** Bloc Client : label « Client » un peu plus marqué. */
    val sidebarClientSubtitle by style {
        property("font-size", "13px")
        property("font-weight", "700")
    }

    /** Bloc Client : nom du client. */
    val sidebarClientName by style {
        property("font-size", "15px")
        property("font-weight", "700")
    }

    /** Bloc Client : email. */
    val sidebarClientEmail by style {
        property("font-size", "13px")
        property("font-weight", "500")
    }

    /** Bloc Client : lien « Tous ses tickets ». */
    val sidebarClientLink by style {
        property("font-size", "12px")
        property("font-weight", "600")
    }

    /** Labels des lignes Mémoire : lisibles sans écraser les valeurs. */
    val sidebarMemoryLabel by style {
        property("font-size", "12.5px")
        property("font-weight", "550")
    }

    /** Bloc Client en tête de carte : fond cohérent, +4px en bas pour mieux respirer. */
    val sidebarClientBlock by style {
        property("margin-top", "0")
        marginTop((-4).px)
        paddingBottom(18.px)
        backgroundColor(Color("#fffff"))
        property("border", "1px solid rgba(15,23,42,0.22)")
    }

    // ----- Carte Météo (dominante) -----

    /** Carte météo : plus grande, plus lisible, accent discret pour la hiérarchie. */
    val sidebarMeteoCard by style {
        backgroundColor(rgba(255, 255, 255, 0.90))
        property("backdrop-filter", "blur(14px)")
        property("-webkit-backdrop-filter", "blur(14px)")
        borderRadius(22.px)
        padding(20.px, 20.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        property("border", "1px solid rgba(15,23,42,0.06)")
        property(
            "box-shadow",
            "0 18px 46px rgba(15,23,42,0.08), 0 4px 14px rgba(15,23,42,0.04), inset 0 1px 0 rgba(255,255,255,0.92)"
        )
        property("transition", "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease")
        property(":hover", "transform: translateY(-1px); box-shadow: 0 22px 58px rgba(15,23,42,0.10), 0 6px 18px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.92); border-color: rgba(15,23,42,0.08);")
        property("min-width", "0")
    }


    // ----- Carte Dossier : mémoire du dossier (caractère + hiérarchie) -----

    /** Carte Dossier : rythme vertical contrôlé par les sections (pas de gap global pour éviter cumuls). */
    val sidebarDossierCard by style {
        backgroundColor(Color(AppStylesTokens.CARD))
        borderRadius(AppStylesTokens.RADIUS_CARD.px)
        padding(16.px)
        paddingBottom(22.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        property("border", "1px solid rgba(15, 23, 42, 0.06)")
        property("box-shadow", AppStylesTokens.SHADOW_CARD)
        property("min-width", "0")
    }

    /** Bloc titre « Mémoire du dossier » + sous-titre « En bref ». */
    val sidebarMemoryTitleBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(1.px)
        paddingBottom(12.px)
        marginBottom(4.px)
    }

    /** Titre principal : porte le rôle de la colonne. */
    val sidebarMemoryTitle by style {
        property("font-size", "13px")
        property("font-weight", "700")
        property("letter-spacing", "-0.01em")
        property("color", "rgba(15,23,42,0.82)")
    }

    /** Sous-titre : En bref. */
    val sidebarMemorySubtitle by style {
        property("font-size", "10px")
        property("font-weight", "500")
        property("letter-spacing", "0.02em")
        property("color", "rgba(15,23,42,0.48)")
    }

    /** Titre de section générique (ex. Documents). */
    val sidebarDossierSectionTitle by style {
        padding(0.px, 0.px)
        paddingBottom(4.px)
         property("font-size", "12.5px")
        property("font-weight", "600")
        property("letter-spacing", "0.03em")
        property("color", "rgba(15,23,42,0.55)")
    }

    /** Ligne titre section repliable : titre à gauche, chevron au bout. Titre → ligne = 8px (via collapseDivider marginTop). */
    val sidebarDossierSectionTitleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        width(100.percent)
    }

    /** Ligne ancrage (1ère info) : point focal de la mémoire. */
    val memoryRowAnchor by style {
        property("background", "rgba(15,23,42,0.045)")
        property("border-left", "3px solid rgba(102,96,230,0.28)")
        property("border-radius", "8px")
    }

    /** Valeur ancrage : plus lisible, dominant. */
    val memoryValueAnchor by style {
        property("font-size", "14px")
        property("font-weight", "700")
        property("color", "rgba(15,23,42,0.92)")
    }

    /** Valeur secondaire : discret, complément. */
    val memoryValueSecondary by style {
        property("font-size", "12px")
        property("font-weight", "600")
        property("color", "rgba(15,23,42,0.68)")
    }

    val memoryLabelSecondary by style {
        property("font-size", "11.5px")
        property("color", "rgba(31,42,68,0.48)")
    }

    /** Données mémoire : police un peu plus marquée (sidebar uniquement, n’affecte pas les autres panneaux). */
    /** Ligne mémoire : alignement sur la baseline pour que label et valeur soient au même niveau. */
    val sidebarMemoryRow by style {
        alignItems(AlignItems.Baseline)
    }

    /** Icône dans la ligne mémoire : reste centrée verticalement malgré align-items baseline. */
    val sidebarMemoryIcon by style {
        property("align-self", "center")
    }

    /** Données mémoire : plus présentes, alignées sur le ton du reste de l’écran. */
    val sidebarMemoryValue by style {
        property("font-size", "14px")
        property("font-weight", "600")
        property("color", "rgba(15,23,42,0.88)")
    }

    /** Nom du document dans la liste : un peu plus lisible. */
    val sidebarDocumentTitle by style {
        property("font-size", "14px")
        property("font-weight", "600")
    }

    /** Bloc de contenu d’une section (Données ou Documents). */
    val sidebarDossierSection by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(0.px)
        property("min-width", "0")
    }

    /** Bloc client → section suivante (liste mémoire) : 24px. */
    val sidebarDossierSectionAfterClient by style {
        property("margin-top", "24px")
    }

    /** Section → section (ex. Preuves après Informations) : 28px. */
    val sidebarDossierSectionAfterSection by style {
        property("margin-top", "28px")
    }

    /** Divider subtil entre sections (espace géré par le gap de la carte). */
    val sidebarDossierDivider by style {
        height(1.px)
        width(100.percent)
        marginTop(0.px)
        marginBottom(0.px)
        property(
            "background",
            "linear-gradient(to right, rgba(15,23,42,0.00), rgba(15,23,42,0.08), rgba(15,23,42,0.00))"
        )
    }

    /** Wrapper du corps d'une section Dossier pour animation expand/collapse (grid 0fr/1fr). */
    val dossierSectionBodyWrap by style {
        display(DisplayStyle.Grid)
        property("transition", "grid-template-rows 220ms ease")
    }

    val dossierSectionBodyCollapsed by style {
        property("grid-template-rows", "0fr")
    }

    val dossierSectionBodyExpanded by style {
        property("grid-template-rows", "1fr")
    }

    /** Enfant direct du grid : min-height 0 pour que 0fr/1fr fonctionne, overflow pour clip. */
    val dossierSectionBodyInner by style {
        minHeight(0.px)
        overflow("hidden")
    }
}
